import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SETTINGS_PATH_EMAIL_DISPATCH } from '../../../utils/settingsRoutes';
import { WORK_REPORT_DOCUMENT_TYPE } from '../utils/documentEmailTemplates';
import { DocumentEmailTemplateSettingsCard } from './email/DocumentEmailTemplateSettingsCard';
import { EmailInboxSettingsCard } from './email/EmailInboxSettingsCard';
import { InboxCategorySettingsCard } from './email/InboxCategorySettingsCard';
import { EmailSignatureSettingsCard } from './email/EmailSignatureSettingsCard';
import {
  clearIncomingInvoiceMailboxPending,
  peekIncomingInvoiceMailboxPending,
} from './email/incomingInvoiceMailboxPending';
import { InboxReplySettingsCard } from './email/InboxReplySettingsCard';
import type { InboxAccount } from './email/types';
import { useModuleAccess } from '../../../hooks/useModuleAccess';

export function EmailSettingsSection() {
  const location = useLocation();
  const navigate = useNavigate();
  // Ohne Posteingang bleiben Postfach-Verbindung, Signatur, Sichtbarkeit und Versandart;
  // Ordner/Kategorien und Antwort-Einstellungen gehören zum gemeinsamen Postfach.
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const inboxEnabled = modulesLoaded && isEnabled('posteingang');
  const emailDispatchPath = React.useMemo(() => {
    const match = location.pathname.match(/^\/i\/[^/]+/);
    const prefix = match ? match[0] : '/i/default';
    return `${prefix}${SETTINGS_PATH_EMAIL_DISPATCH}`;
  }, [location.pathname]);

  const [inboxAccounts, setInboxAccounts] = React.useState<InboxAccount[]>([]);
  const [inboxLoading, setInboxLoading] = React.useState(false);
  const [inboxError, setInboxError] = React.useState<string | null>(null);
  const [inboxNotice, setInboxNotice] = React.useState<string | null>(null);

  const loadInboxAccounts = React.useCallback(() => {
    setInboxLoading(true);
    return fetch('/api/inbox/accounts')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? (data as InboxAccount[]) : [];
        setInboxAccounts(list);
        return list;
      })
      .catch(() => {
        setInboxAccounts([]);
        return [] as InboxAccount[];
      })
      .finally(() => setInboxLoading(false));
  }, []);

  React.useEffect(() => {
    void loadInboxAccounts();
  }, [loadInboxAccounts]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nylasGrant = params.get('nylas_grant');
    const mailError = params.get('mail_error');
    if (nylasGrant || params.get('mail_connected') === '1') {
      setInboxError(null);
      const connectedEmail = params.get('connected_email');
      setInboxNotice(
        connectedEmail
          ? `Postfach ${connectedEmail} wurde verbunden.`
          : 'Postfach wurde verbunden.',
      );
      const pending = peekIncomingInvoiceMailboxPending();
      void loadInboxAccounts().then(async (list) => {
        if (!pending) return;
        const needle = (connectedEmail || pending.email || '').trim().toLowerCase();
        if (!needle) {
          clearIncomingInvoiceMailboxPending();
          return;
        }
        const match = list.find((acc) => String(acc.email || '').trim().toLowerCase() === needle);
        if (!match) {
          clearIncomingInvoiceMailboxPending();
          return;
        }
        try {
          const res = await fetch(`/api/inbox/accounts/${encodeURIComponent(String(match.id))}/incoming-invoice`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ incoming_invoice_ingest: true }),
          });
          clearIncomingInvoiceMailboxPending();
          if (!res.ok) return;
          navigate(pending.returnPath, { replace: true });
        } catch {
          clearIncomingInvoiceMailboxPending();
        }
      });
      window.history.replaceState({}, '', emailDispatchPath);
      return;
    }
    if (mailError) {
      clearIncomingInvoiceMailboxPending();
      setInboxError(decodeURIComponent(mailError));
      window.history.replaceState({}, '', emailDispatchPath);
    }
  }, [location.search, loadInboxAccounts, emailDispatchPath, navigate]);

  React.useEffect(() => {
    if (location.hash !== '#kategorien') return;
    const el = document.getElementById('kategorien');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, inboxAccounts.length]);

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      {inboxNotice ? (
        <div className="p-3 rounded-lg bg-green-50 text-green-800 text-sm border border-green-200 flex justify-between items-center gap-3">
          <span>{inboxNotice}</span>
          <button type="button" onClick={() => setInboxNotice(null)} className="text-green-700 hover:underline shrink-0">
            Schließen
          </button>
        </div>
      ) : null}
      {inboxError ? (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex justify-between items-center gap-3">
          <span>{inboxError}</span>
          <button type="button" onClick={() => setInboxError(null)} className="text-red-500 hover:underline shrink-0">
            Schließen
          </button>
        </div>
      ) : null}

      <EmailInboxSettingsCard
        accounts={inboxAccounts}
        loading={inboxLoading}
        onReload={loadInboxAccounts}
        onError={setInboxError}
      />

      {inboxEnabled ? <InboxCategorySettingsCard accounts={inboxAccounts} /> : null}

      <EmailSignatureSettingsCard inboxAccounts={inboxAccounts} onAccountsChange={setInboxAccounts} />

      {inboxEnabled ? <InboxReplySettingsCard /> : null}

      <DocumentEmailTemplateSettingsCard documentType={WORK_REPORT_DOCUMENT_TYPE} />
    </div>
  );
}
